import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { extractApiError } from '@/services/errors';
import { applyProposal, createProposal, getPresets, swapProposal } from '@/services/simulados';
import { PresetPicker } from './PresetPicker';
import { ProposalDraft } from './ProposalDraft';

export function BalancedStep({ taxonomyIds, onCreateEmpty, onCreateBook, onDone }) {
  const [mode, setMode] = useState(null); // null | 'manual' | 'balanced'
  const [presets, setPresets] = useState([]);
  const [presetKey, setPresetKey] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [swappingQuestionId, setSwappingQuestionId] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const excludedIdsRef = useRef([]);

  useEffect(() => {
    if (mode !== 'balanced' || presets.length) return;
    getPresets()
      .then(data => setPresets(data.presets || []))
      .catch(err => toast.error(extractApiError(err, 'Erro ao carregar presets')));
  }, [mode, presets.length]);

  const selectedIdsPlusExcluded = items => [...items.map(i => i.question_id), ...excludedIdsRef.current.filter(id => !items.some(i => i.question_id === id))];

  const generate = async () => {
    setGenerating(true);
    try {
      excludedIdsRef.current = [];
      setProposal(await createProposal({ preset: presetKey, taxonomyIds }));
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao gerar o simulado'));
    } finally {
      setGenerating(false);
    }
  };

  const swapItem = async item => {
    setSwappingQuestionId(item.question_id);
    try {
      const { item: replacement } = await swapProposal({
        preset: presetKey,
        taxonomyIds,
        descriptorCode: item.descriptor_code,
        selectedQuestionIds: selectedIdsPlusExcluded(proposal.items)
      });
      excludedIdsRef.current = [...excludedIdsRef.current, item.question_id];
      setProposal(prev => ({ ...prev, items: prev.items.map(i => (i.question_id === item.question_id ? replacement : i)) }));
    } catch (err) {
      toast.error(extractApiError(err, 'Sem questão disponível para este descritor'));
    } finally {
      setSwappingQuestionId(null);
    }
  };

  const applyWithRetry = async (bookId, items) => {
    const payload = items.map(i => ({ question_id: i.question_id, taxonomy_id: i.taxonomy_id }));
    try {
      await applyProposal(bookId, payload);
      return items;
    } catch (err) {
      const burned = err?.response?.status === 409 ? err.response.data?.burned_question_ids || [] : null;
      if (!burned) throw err;
      let currentItems = items;
      for (const burnedId of burned) {
        const burnedItem = currentItems.find(i => i.question_id === burnedId);
        const { item: replacement } = await swapProposal({
          preset: presetKey,
          taxonomyIds,
          descriptorCode: burnedItem.descriptor_code,
          selectedQuestionIds: selectedIdsPlusExcluded(currentItems)
        });
        excludedIdsRef.current = [...excludedIdsRef.current, burnedId];
        currentItems = currentItems.map(i => (i.question_id === burnedId ? replacement : i));
      }
      await applyProposal(
        bookId,
        currentItems.map(i => ({ question_id: i.question_id, taxonomy_id: i.taxonomy_id }))
      );
      return currentItems;
    }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const book = await onCreateBook([...new Set(proposal.items.map(i => i.taxonomy_id))]);
      if (!book) return;
      const finalItems = await applyWithRetry(book.id, proposal.items);
      setProposal(prev => ({ ...prev, items: finalItems }));
      onDone(book.id);
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao aplicar o simulado no caderno'));
    } finally {
      setConfirming(false);
    }
  };

  if (mode === null) {
    return (
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => setMode('balanced')}
        >
          Gerar balanceado
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onCreateEmpty}
        >
          Criar vazio e escolher depois
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PresetPicker
        presets={presets}
        value={presetKey}
        onChange={setPresetKey}
      />
      {!proposal && (
        <Button
          type="button"
          disabled={!presetKey || generating}
          onClick={generate}
        >
          {generating ? 'Gerando…' : 'Gerar simulado'}
        </Button>
      )}
      {proposal && (
        <ProposalDraft
          proposal={proposal}
          onSwap={swapItem}
          swappingQuestionId={swappingQuestionId}
          onConfirm={confirm}
          confirming={confirming}
        />
      )}
    </div>
  );
}
